import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/Common";
import { PlanCards } from "@/components/PlanCards";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr, isMember, fmtDate } from "@/lib/api";
import { isIOSApp, originUrl, openExternal } from "@/lib/native";

export default function Membership() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const m = user?.membership || {};
  const active = isMember(user);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setUsage(r.data.interview_usage));
    if (m.stripe_subscription_id || params.get("synced")) api.post("/payments/sync").then(() => refresh());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const manage = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: originUrl() });
      await openExternal(data.url);
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  const periodEnd = m.current_period_end ? new Date(m.current_period_end * 1000).toLocaleDateString() : null;

  return (
    <div>
      <PageHeader eyebrow="Billing" title="Membership" desc="Your membership unlocks hiring, interviews, offers and our business file library." />
      <div data-testid="membership-status-card" className={`mb-12 rounded-md border p-6 sm:p-8 ${active ? "border-emerald-200 bg-emerald-50" : "border-stone-200 bg-white"}`}>
        <div className="flex flex-wrap items-start justify-between gap-6">
          {active ? (
            <div className="flex items-start gap-4">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              <div>
                <p className="font-display text-xl font-bold capitalize text-stone-900" data-testid="membership-plan-name">{m.plan} plan · Active</p>
                <p className="mt-1 text-sm text-stone-600">Member since {fmtDate(m.activated_at)}{periodEnd && ` · ${m.cancel_at_period_end ? "Ends" : "Renews"} ${periodEnd}`}</p>
                {m.cancel_at_period_end && <p className="mt-1 text-sm font-medium text-amber-800" data-testid="membership-cancel-notice">Cancellation scheduled. You keep access until the end of this period.</p>}
                {usage && <p className="mt-3 text-sm text-stone-700" data-testid="interview-usage">Interview requests this month: <b>{usage.used}{usage.limit != null ? ` / ${usage.limit}` : " (unlimited)"}</b></p>}
              </div>
            </div>
          ) : (
            <div>
              <p className="font-display text-xl font-bold text-stone-900" data-testid="membership-plan-name">{m.status === "cancelled" ? "Membership cancelled" : m.status === "past_due" ? "Payment overdue" : "No active membership"}</p>
              <p className="mt-1 text-sm text-stone-600">Choose a plan below. Secure checkout powered by Stripe.</p>
            </div>
          )}
          {m.stripe_customer_id && !isIOSApp && (
            <Button data-testid="manage-subscription-button" disabled={busy} onClick={manage} variant="outline" className="bg-white">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />}Upgrade, downgrade or cancel
            </Button>
          )}
        </div>
        {active && !m.stripe_customer_id && <p className="mt-4 text-sm text-stone-600" data-testid="manual-membership-note">Your membership was set up by our team. Message us to change or cancel your plan.</p>}
      </div>
      {(!active || !m.stripe_customer_id) && <PlanCards currentPlan={active ? m.plan : null} />}
    </div>
  );
}
